import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useHistory } from 'react-router';
import { useForm } from 'react-hook-form';

import { NewsFeedLogin } from '../../components/content/newsFeedLogin';

import userService from '../../services/UserService/user-service.js';
import { LoginHeader } from '../../components/header/loginHeader.jsx';
import { useAuthPageLayout } from '../../hooks/useAuthPageLayout.js';
import { FormField } from '../../components/user-form/form-field.jsx';

import { useTranslation, Trans } from 'react-i18next';

const MAX_STEPS = 3;

export default function Register(props) {
	const { push } = useHistory();
	const settings = useAuthPageLayout();
	const { t, i18n } = useTranslation();
	const [ countriesList, setCountriesList ] = useState([]);
	const [ titlesList, setTitlesList ] = useState([]);
	const [ formStep, setFormStep ] = useState(0);

	const { register, handleSubmit, reset, formState: { errors, isSubmitting, isValid }, watch } = useForm({ mode: "onTouched" });

	const registerOptions = {
		name: { required: t('registerOptions.obrigatorio') },
		username: { 
			required: t('registerOptions.obrigatorio'),
			minLength: {
				value: 4,
				message: t('registerOptions.minDigito', {x: 4})
			}
		},
		email: { 
			required: t('registerOptions.obrigatorio'),
			pattern: {
				value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
				message: t('registerOptions.nãoÉVálido')
			}
		},
		password: {
		  required: t('registerOptions.obrigatorio'),
		  minLength: {
			value: 8,
			message: t('registerOptions.minDigito', {x: 8})
		  },
		},
		confirmPwd: {
			validate: (val) => {
				if (watch('password') != val) {
				  return t('registerOptions.naoConfere');
				}
			  }
		},
		institution: {
			required: t('registerOptions.obrigatorio'),
		},
		country_id: {
			validate: (val) => {
				return val != -1 || t('registerOptions.obrigatorio')
			}
		},
		title_id: {
			validate: (val) => {
				return val != -1 || t('registerOptions.obrigatorio')
			}
		},
		academic: {
			required: t('registerOptions.obrigatorio'),
		},
		interest: {
			required: t('registerOptions.obrigatorio'),
		}
	  };

	useEffect(() => {
		let isMounted = true;
		userService.listCountries().then( (response) =>{
			if (isMounted) setCountriesList( response.data );
		});
		userService.listTitles().then( (response) =>{
			if (isMounted) setTitlesList( response.data );
		});

		return () => {
			isMounted = false;
		}
	}, [])

	const normalizeData = (data) =>
		Object.fromEntries(
			Object.entries(data).map(([k, v]) => { 
				if ((k === 'country_id' || k==='title_id') && v === -1) return [k, null];
				return [k,v]; 
			}).filter(([k]) => k !== 'confirmPwd')
		)

	const handleFormSubmit = (data) => { 
 		return userService.register (
			normalizeData(data)
		).then( () => {
			settings.handleSetAlert('success', t('alerta.titulo.sucesso'), t('user.cadastro.alerta.sucessoRegistro'));
			push('/');
		}).catch( (err) => {
			reset({
				password: '',
				confirmPwd: '',
			})
			if(err.request.status === 400){
				settings.handleSetAlert('danger', t('alerta.titulo.erro'), t('user.cadastro.alerta.faltaInfo'));
			}else{
				settings.handleSetAlert('danger', t('alerta.titulo.erro'), t('alerta.algoDeuErrado.mensagem'));
			}
		}); 
	}

	const completeFormStep = (data) => {
		if(formStep<2) return setFormStep(current => current + 1);
		else return handleFormSubmit(data);
	}

	const handleFormBack = () => {
		if(formStep>0) setFormStep(current => current - 1);
	}

	return (
		<div className="register register-with-news-feed">
			<div className="register-container">
				<LoginHeader title={t('user.cadastro.titulo')} />

				<div className="register-content">
					<form onSubmit={handleSubmit(completeFormStep)} className="fs-13px">
						<div><p>
							{formStep > 0 && 
								<>
									<a href="#" onClick={handleFormBack} className="link-info">
										&le; {t('user.cadastro.botão.voltar')}
									</a>{' '} 
								</>
							}
							{t('user.cadastro.passos', { atual: formStep+1, total:  MAX_STEPS })}
						</p></div>
						{formStep === 0 && 
							<section>
								<FormField name="name" label={t('userInfo.nome')} placeholder="Nome" register={register} options={registerOptions.name} error={errors?.name} />
								<FormField name="username" label={t('userInfo.apelido')} placeholder="Login" register={register} options={registerOptions.username} error={errors?.username} />
								<FormField name="email" label={t('userInfo.email')} placeholder="Email" register={register} options={registerOptions.email} error={errors?.email} />
								<FormField name="password" type="password" label={t('userInfo.senha')} placeholder="Senha" register={register} options={registerOptions.password} error={errors?.password} showPasswordLabel={t('botão.mostrarSenha')} hidePasswordLabel={t('botão.ocultarSenha')}  />
								<FormField name="confirmPwd" type="password" label={t('userInfo.confirmeSenha')} placeholder="Confirme a senha" register={register} options={registerOptions.confirmPwd} error={errors?.confirmPwd} showPasswordLabel={t('botão.mostrarSenha')} hidePasswordLabel={t('botão.ocultarSenha')} />
							</section>
						}
						
						{formStep === 1 && 
							<section>
								<FormField name="institution" label={t('userInfo.instituição')} placeholder="Instituição" register={register} options={registerOptions.institution} error={errors?.institution} />
								<FormField as="select" name="country_id" label={t('dropDown.país')} defaultValue={-1}
									register={register} options={registerOptions.country_id} error={errors?.country_id}>
									<option disabled value={-1}></option>
									{countriesList.map(c => (<option key={c.id} value={c.id}>{c.name}</option>))}
								</FormField>
								<FormField as="select" name="title_id" label={t('dropDown.título')} defaultValue={-1}
									register={register} options={registerOptions.title_id} error={errors?.title_id}>
									<option disabled value={-1}></option>
									{titlesList.map(c => (<option key={c.id} value={c.id}>{c[`name_${i18n.language}`]}</option>))}
								</FormField>
							</section>
						}
						

						{formStep === 2 && 
							<section>
								<FormField as="textarea" name="academic" label={t('userInfo.formaçãoAcadêmica')} placeholder="Descreva resumidamente sua formação acadêmica."
									register={register} options={registerOptions.academic} error={errors?.academic} />
								<FormField as="textarea" name="interest" label={t('userInfo.areasDeInteresse')} placeholder="Apresente suas áreas de interesse em Arqueologia e Arqueobotânica."
									register={register} options={registerOptions.interest} error={errors?.interest} />
							</section>
						}
						<div className="mb-4">
							<button type="submit" 
									disabled={isSubmitting || !isValid}
									className="btn btn-primary d-block w-100 btn-lg h-35px fs-13px" >
								{isSubmitting && (<i className="fas fa-spinner fa-pulse m-2"></i>)}
								{formStep < 2 &&
									t('user.cadastro.botão.continuar')}
								{formStep === 2 &&
									t('user.cadastro.botão.finalizar')}
							</button>
						</div>
						
					</form>


					<div className="mb-4 pb-5" >
						<Trans i18nKey='user.link.acesso'>		
							Já possui uma conta? Clique <Link to="/acesso" className="text-info">aqui</Link> para acessar o sistema.
						</Trans>
							<br></br>
						<Trans i18nKey='user.link.voltarInicio'>	
							Para Voltar a página inicial como convidade clique <Link to="/" className="text-info">aqui</Link>.
						</Trans>
					</div>
				</div>
			</div>
			
			<NewsFeedLogin bgImg='url(/assets/img/login-bg/login-2.jpeg)' />
		</div>	
	)
}